import React, { useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'

const API_URL = process.env.REACT_APP_API_URL

function parseApiResponse(raw) {
  let d = raw
  if (typeof d === 'string') {
    try {
      d = JSON.parse(d)
    } catch {
      return raw
    }
  }
  if (d?.body != null) {
    if (typeof d.body === 'string') {
      try {
        return JSON.parse(d.body)
      } catch {
        return d.body
      }
    }
    return d.body
  }
  return d
}

function extractExcelBase64(raw) {
  if (raw == null) return null
  if (typeof raw === 'string') {
    const t = raw.trim()
    if (t.startsWith('UEs')) return raw
    if (t.startsWith('{')) {
      try {
        return extractExcelBase64(JSON.parse(t))
      } catch {
        return null
      }
    }
    return null
  }
  if (typeof raw === 'object') {
    if (raw.body != null) return extractExcelBase64(raw.body)
    if (typeof raw.data === 'string') return extractExcelBase64(raw.data)
    if (raw.data != null && typeof raw.data === 'object') {
      const nested = extractExcelBase64(raw.data)
      if (nested) return nested
    }
    if (raw.file != null) return extractExcelBase64(raw.file)
    if (raw.base64 != null) return extractExcelBase64(raw.base64)
  }
  return null
}

function extractDownloadUrl(raw) {
  if (!raw || typeof raw !== 'object') return ''
  const candidates = [
    raw.downloadUrl,
    raw.url,
    raw.fileUrl,
    raw.presignedUrl,
    raw.data?.downloadUrl,
    raw.data?.url,
    raw.data?.fileUrl,
    raw.data?.presignedUrl
  ]
  for (const value of candidates) {
    const url = String(value || '').trim()
    if (/^https?:\/\//i.test(url)) return url
  }
  return ''
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== 'object') return false
  const success = responseData.success
  return success === true || success === 'true' || success === 1 || success === '1'
}

function getResultData(responseData) {
  if (!responseData || typeof responseData !== 'object') return {}
  if (responseData.results && typeof responseData.results === 'object') {
    return responseData.results
  }
  if (responseData.data?.results && typeof responseData.data.results === 'object') {
    return responseData.data.results
  }
  if (responseData.data && typeof responseData.data === 'object') {
    return responseData.data
  }
  return responseData
}

function getCreatedCount(resultData) {
  const direct = Number(resultData?.created ?? resultData?.imported ?? resultData?.success)
  if (Number.isFinite(direct) && direct >= 0) return direct
  const list =
    resultData?.createdItems ||
    resultData?.importedRecords ||
    resultData?.imported ||
    []
  return Array.isArray(list) ? list.length : 0
}

function getSkippedCount(resultData) {
  const direct = Number(resultData?.skipped)
  if (Number.isFinite(direct) && direct >= 0) return direct
  return Array.isArray(resultData?.skippedItems) ? resultData.skippedItems.length : 0
}

function getFailedCount(resultData) {
  const direct = Number(resultData?.failed)
  if (Number.isFinite(direct) && direct >= 0) return direct
  return Array.isArray(resultData?.errors) ? resultData.errors.length : 0
}

function getTotalCount(resultData, created, skipped, failed) {
  const direct = Number(resultData?.total)
  if (Number.isFinite(direct) && direct >= 0) return direct
  return created + skipped + failed
}

function buildImportSummaryMessage(responseData, resultData) {
  const created = getCreatedCount(resultData)
  const skipped = getSkippedCount(resultData)
  const failed = getFailedCount(resultData)
  const apiMessage = String(responseData?.message || resultData?.message || '').trim()
  const countsText = `Created ${created}, skipped ${skipped}, failed ${failed}`

  if (apiMessage) {
    const hasCounts =
      /created\s*\d+/i.test(apiMessage) ||
      /skipped\s*\d+/i.test(apiMessage) ||
      /failed\s*\d+/i.test(apiMessage)
    return hasCounts ? apiMessage : `${apiMessage} ${countsText}`
  }

  return `Sales order import completed. ${countsText}`
}

function getCreatedRecords(resultData) {
  if (Array.isArray(resultData?.createdItems) && resultData.createdItems.length > 0) {
    return resultData.createdItems
  }
  if (Array.isArray(resultData?.importedRecords) && resultData.importedRecords.length > 0) {
    return resultData.importedRecords
  }
  if (Array.isArray(resultData?.imported) && resultData.imported.length > 0) {
    return resultData.imported
  }
  return []
}

function getSkippedRecords(resultData) {
  if (Array.isArray(resultData?.skippedItems)) return resultData.skippedItems
  if (Array.isArray(resultData?.skippedRecords)) return resultData.skippedRecords
  return []
}

function getErrorRecords(resultData) {
  if (Array.isArray(resultData?.errors)) return resultData.errors
  if (Array.isArray(resultData?.failedItems)) return resultData.failedItems
  return []
}

function triggerXlsxDownloadFromBase64(
  base64,
  filenamePrefix = 'Sales_Order_Import_Template'
) {
  if (!base64 || typeof base64 !== 'string' || !base64.startsWith('UEs')) {
    throw new Error('Invalid Excel data received')
  }
  const byteCharacters = atob(base64)
  const byteNumbers = new Array(byteCharacters.length)
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i)
  }
  const byteArray = new Uint8Array(byteNumbers)
  const blob = new Blob([byteArray], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)
  link.download = `${filenamePrefix}-${timestamp}.xlsx`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

const SalesOrderImport = () => {
  const [selectedFile, setSelectedFile] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [importResults, setImportResults] = useState(null)
  const fileInputRef = useRef(null)
  const [isDownloadingSample, setIsDownloadingSample] = useState(false)

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      if (!file) {
        reject(new Error('No file selected'))
        return
      }

      const maxSize = 10 * 1024 * 1024
      if (file.size > maxSize) {
        reject(new Error('File size exceeds 10MB limit'))
        return
      }

      if (file.size === 0) {
        reject(new Error('File is empty or cannot be read'))
        return
      }

      const reader = new FileReader()

      reader.onload = () => {
        try {
          const base64String = reader.result.split(',')[1]
          if (!base64String) {
            reject(new Error('Failed to convert file to base64'))
            return
          }
          resolve(base64String)
        } catch (error) {
          reject(new Error('Error processing file: ' + error.message))
        }
      }

      reader.onerror = (error) => {
        let errorMessage = 'Failed to read file. '
        if (error.target && error.target.error) {
          const errorCode = error.target.error.code
          const NOT_FOUND_ERR = 1
          const NOT_READABLE_ERR = 2
          const ABORT_ERR = 3

          switch (errorCode) {
            case NOT_FOUND_ERR:
              errorMessage += 'File not found.'
              break
            case NOT_READABLE_ERR:
              errorMessage +=
                'File is not readable. Please ensure the file is not open in another program and try again.'
              break
            case ABORT_ERR:
              errorMessage += 'File reading was aborted.'
              break
            default:
              errorMessage +=
                'An error occurred while reading the file. Please try selecting the file again.'
          }
        } else {
          errorMessage += 'Please try selecting the file again.'
        }
        reject(new Error(errorMessage))
      }

      reader.onabort = () => {
        reject(new Error('File reading was aborted'))
      }

      try {
        reader.readAsDataURL(file)
      } catch (error) {
        reject(new Error('Failed to start reading file: ' + error.message))
      }
    })
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (file) {
      const validExtensions = ['.xlsx', '.xls']
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase()

      if (!validExtensions.includes(fileExtension)) {
        toast.error('Please upload an Excel file (.xlsx or .xls)', { autoClose: 2000 })
        event.target.value = ''
        setSelectedFile(null)
        return
      }
      setSelectedFile(file)
      setImportResults(null)
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (!selectedFile) {
      toast.error('Please select a file to import', { autoClose: 2000 })
      return
    }

    const userId = localStorage.getItem('id')
    if (!userId) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      return
    }

    setIsLoading(true)
    setImportResults(null)

    try {
      let fileBase64
      try {
        fileBase64 = await fileToBase64(selectedFile)
      } catch (fileError) {
        toast.error(fileError.message || 'Failed to read file. Please try again.', {
          autoClose: 3000
        })
        setIsLoading(false)
        setSelectedFile(null)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        return
      }

      const payload = {
        action: 'import-sales-orders',
        fileBase64,
        fileName: selectedFile.name,
        currentUserId: userId
      }

      const response = await axios.post(API_URL, payload)
      const responseData = parseApiResponse(response.data)
      const resultData = getResultData(responseData)
      const created = getCreatedCount(resultData)
      const skipped = getSkippedCount(resultData)
      const failed = getFailedCount(resultData)
      const total = getTotalCount(resultData, created, skipped, failed)
      const summaryMessage = buildImportSummaryMessage(responseData, resultData)

      const normalizedResults = {
        ...resultData,
        total,
        created,
        skipped,
        failed,
        imported: created,
        resultMessage: summaryMessage,
        createdItems: getCreatedRecords(resultData),
        skippedItems: getSkippedRecords(resultData),
        errors: getErrorRecords(resultData)
      }

      if (isApiSuccess(responseData)) {
        setImportResults(normalizedResults)

        if (failed > 0 && created === 0) {
          toast.error(summaryMessage || 'No sales orders were imported', {
            autoClose: 3500
          })
        } else {
          toast.success(summaryMessage, { autoClose: 3500 })
        }

        setSelectedFile(null)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      } else {
        const hasCounts =
          created > 0 || skipped > 0 || failed > 0 || total > 0
        if (hasCounts) {
          setImportResults(normalizedResults)
        }
        toast.error(summaryMessage || responseData.message || 'Import failed', {
          autoClose: 3500
        })
      }
    } catch (error) {
      console.error('Import error:', error)
      if (error.response && error.response.data) {
        const errorData = parseApiResponse(error.response.data)
        const resultData = getResultData(errorData)
        const summaryMessage = buildImportSummaryMessage(errorData, resultData)
        const created = getCreatedCount(resultData)
        const skipped = getSkippedCount(resultData)
        const failed = getFailedCount(resultData)
        const total = getTotalCount(resultData, created, skipped, failed)

        if (created > 0 || skipped > 0 || failed > 0 || total > 0) {
          setImportResults({
            ...resultData,
            total,
            created,
            skipped,
            failed,
            imported: created,
            resultMessage: summaryMessage,
            createdItems: getCreatedRecords(resultData),
            skippedItems: getSkippedRecords(resultData),
            errors: getErrorRecords(resultData)
          })
        }

        toast.error(
          summaryMessage || errorData.message || 'An error occurred during import',
          { autoClose: 3500 }
        )
      } else {
        toast.error('Network error. Please check your connection and try again.', {
          autoClose: 2000
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleDownloadSample = async () => {
    if (isDownloadingSample) return

    const currentUserId = localStorage.getItem('id')
    if (!currentUserId) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      return
    }

    setIsDownloadingSample(true)

    try {
      const response = await axios.post(API_URL, {
        action: 'download-sales-order-import-template',
        currentUserId
      })

      const responseData = parseApiResponse(response.data)
      const downloadUrl = extractDownloadUrl(responseData)
      if (downloadUrl) {
        const link = document.createElement('a')
        link.href = downloadUrl
        link.rel = 'noopener noreferrer'
        document.body.appendChild(link)
        link.click()
        link.remove()
        toast.success('Sample Excel downloaded successfully', { autoClose: 2000 })
        return
      }

      const base64 = extractExcelBase64(response.data)
      if (!base64) {
        toast.error(responseData?.message || 'Failed to download sample Excel', {
          autoClose: 2000
        })
        return
      }

      triggerXlsxDownloadFromBase64(base64.trim(), 'Sales_Order_Import_Template')
      toast.success('Sample Excel downloaded successfully', { autoClose: 2000 })
    } catch (error) {
      console.error('Sample download error:', error)
      if (error.response && error.response.data) {
        const errorData = parseApiResponse(error.response.data)
        toast.error(
          errorData.message || 'An error occurred while downloading sample file',
          { autoClose: 2000 }
        )
      } else {
        toast.error('Network error. Please check your connection and try again.', {
          autoClose: 2000
        })
      }
    } finally {
      setIsDownloadingSample(false)
    }
  }

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">Import Sales Orders</h1>
            </div>
            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <Link to="/salesOrder">
                    <i className="fa-solid fa-arrow-left-long" /> Back
                  </Link>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="frmwdth_cntr_640">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-header">
                      <h3 className="card-title">Upload Excel File</h3>
                    </div>
                    <form className="form-horizontal" onSubmit={handleSubmit}>
                      <div className="card-body">
                        <div className="form-group">
                          <div className="input-group">
                            <input
                              type="text"
                              className="form-control"
                              value={selectedFile ? selectedFile.name : ''}
                              placeholder="Choose file"
                              readOnly
                            />
                            <div className="input-group-append">
                              <label
                                className="btn btn-blue mb-0"
                                htmlFor="soImportFile"
                                style={{ cursor: 'pointer' }}
                              >
                                Browse
                              </label>
                              <input
                                ref={fileInputRef}
                                type="file"
                                className="d-none"
                                id="soImportFile"
                                accept=".xlsx,.xls"
                                onChange={handleFileChange}
                              />
                            </div>
                          </div>
                          <small className="form-text text-muted mt-2">
                            Only Excel files (.xlsx, .xls) are supported.
                          </small>
                          <div className="mt-2">
                            <button
                              type="button"
                              className="btn btn-link p-0"
                              onClick={handleDownloadSample}
                              disabled={isDownloadingSample}
                            >
                              {isDownloadingSample
                                ? 'Preparing download...'
                                : 'Download sample Excel format'}
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="card-footer text-center">
                        <button
                          type="submit"
                          className="btn btn-blue"
                          disabled={!selectedFile || isLoading}
                        >
                          {isLoading ? 'Importing...' : 'Import'}
                        </button>
                      </div>
                    </form>
                  </div>

                  {importResults && (
                    <div className="card card-primary card-outline mt-3">
                      <div className="card-header">
                        <h3 className="card-title">Import Results</h3>
                      </div>
                      <div className="card-body import-results">
                        {importResults.resultMessage ? (
                          <div
                            className={`alert ${
                              Number(importResults.failed || 0) > 0
                                ? 'alert-warning'
                                : 'alert-success'
                            } mb-3`}
                          >
                            {importResults.resultMessage}
                          </div>
                        ) : null}

                        <div className="row mb-3">
                          <div className="col-lg-3 col-md-6 col-sm-6 mb-3">
                            <div className="info-box">
                              <span className="info-box-icon bg-info elevation-1">
                                <i className="fa-solid fa-list"></i>
                              </span>
                              <div className="info-box-content">
                                <span className="info-box-text">Total Records</span>
                                <span className="info-box-number">
                                  {importResults.total || 0}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="col-lg-3 col-md-6 col-sm-6 mb-3">
                            <div className="info-box">
                              <span className="info-box-icon bg-primary elevation-1">
                                <i className="fa-solid fa-plus"></i>
                              </span>
                              <div className="info-box-content">
                                <span className="info-box-text">Created</span>
                                <span className="info-box-number">
                                  {importResults.created || 0}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="col-lg-3 col-md-6 col-sm-6 mb-3">
                            <div className="info-box">
                              <span className="info-box-icon bg-warning elevation-1">
                                <i className="fa-solid fa-exclamation"></i>
                              </span>
                              <div className="info-box-content">
                                <span className="info-box-text">Skipped Records</span>
                                <span className="info-box-number">
                                  {importResults.skipped || 0}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="col-lg-3 col-md-6 col-sm-6 mb-3">
                            <div className="info-box">
                              <span className="info-box-icon bg-danger elevation-1">
                                <i className="fa-solid fa-times"></i>
                              </span>
                              <div className="info-box-content">
                                <span className="info-box-text">Failed Records</span>
                                <span className="info-box-number">
                                  {importResults.failed || 0}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {importResults.createdItems?.length > 0 ? (
                          <div className="mb-4">
                            <h5 className="mb-3 text-success">Created Records:</h5>
                            <div
                              className="table-responsive"
                              style={{
                                maxHeight: '400px',
                                overflowY: 'auto',
                                border: '1px solid #dee2e6'
                              }}
                            >
                              <table className="table table-bordered table-striped mb-0">
                                <thead
                                  className="thead-light"
                                  style={{
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 10,
                                    backgroundColor: '#f8f9fa'
                                  }}
                                >
                                  <tr>
                                    <th>Row</th>
                                    <th>CPO Number</th>
                                    <th>Customer Name</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {importResults.createdItems.map((record, index) => (
                                    <tr key={record.id || index}>
                                      <td>{record.row || index + 1}</td>
                                      <td>{record.cpoNumber || '-'}</td>
                                      <td>{record.customerName || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ) : null}

                        {importResults.skippedItems?.length > 0 ? (
                          <div className="mb-4">
                            <h5 className="mb-3 text-warning">Skipped Records:</h5>
                            <div
                              className="table-responsive"
                              style={{
                                maxHeight: '400px',
                                overflowY: 'auto',
                                border: '1px solid #dee2e6'
                              }}
                            >
                              <table className="table table-bordered table-striped mb-0">
                                <thead
                                  className="thead-light"
                                  style={{
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 10,
                                    backgroundColor: '#f8f9fa'
                                  }}
                                >
                                  <tr>
                                    <th>Row</th>
                                    <th>CPO Number</th>
                                    <th>Reason</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {importResults.skippedItems.map((record, index) => (
                                    <tr key={index}>
                                      <td>{record.row || index + 1}</td>
                                      <td>{record.cpoNumber || '-'}</td>
                                      <td>
                                        {record.reason || record.message || record.error || '-'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ) : null}

                        {Number(importResults.failed || 0) > 0 &&
                        importResults.errors?.length > 0 ? (
                          <div>
                            <h5 className="mb-3 text-danger">Errors:</h5>
                            <div
                              className="table-responsive"
                              style={{
                                maxHeight: '400px',
                                overflowY: 'auto',
                                border: '1px solid #dee2e6'
                              }}
                            >
                              <table className="table table-bordered table-striped mb-0">
                                <thead
                                  className="thead-light"
                                  style={{
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 10,
                                    backgroundColor: '#f8f9fa'
                                  }}
                                >
                                  <tr>
                                    <th>Row</th>
                                    <th>CPO Number</th>
                                    <th>Customer Name</th>
                                    <th>Error</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {importResults.errors.map((error, index) => (
                                    <tr key={index}>
                                      <td>{error.row || index + 1}</td>
                                      <td>{error.cpoNumber || '-'}</td>
                                      <td>{error.customerName || '-'}</td>
                                      <td className="text-danger">
                                        {error.error || error.message || error.reason || '-'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SalesOrderImport